import type { DashboardMode } from '@/interfaces/Dashboard.interface';
import { UiFieldComponent } from '@/ui/field/ui-field';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { ApplyPureFunctionPipe } from '@/utils/angular/pipes/apply-pure-function.pipe';
import { FeatureCode } from '@/utils/disabled-features/disabled-features.interface';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import { translate } from '@/utils/i18n/translate.reactive-helpers';
import { ReferenceListLabelPipe } from '@/utils/reference-lists/pipes/reference-list-label.pipe';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { debounceTimeAllButFirst } from '@/utils/rxjs/operators/debounce-time-all-but-first.operator';
import { frenchSort } from '@/utils/toolkit/array/sort.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, inject, type OnDestroy, type OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, type Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AdvizeoInputDirective } from '../../../directives/advizeo-input/advizeo-input.directive';
import { AdvizeoTooltipDirective } from '../../../directives/advizeo-tooltip/advizeo-tooltip.directive';
import type { DashboardScopeType } from '../../../interfaces/Dashboard.interface';
import {
    type TailoredWidget,
    type TailoredWidgetId,
    type TailoredWidgetTemplate,
    TailoredWidgetTemplateCode,
    type TailoredWidgetTemplateId,
    TailoredWidgetType
} from '../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from '../../../providers/business-intelligence/business-intelligence.provider';
import { SearchProvider } from '../../../providers/search/search.provider';
import { TailoredWidgetsProvider } from '../../../providers/tailored-widgets/tailored-widgets.provider';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../../advizeo-modal-spawner/advizeo-modal-spawner.component';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { WidgetCatalogItemComponent } from '../widget-catalog-item/widget-catalog-item';

export interface WidgetPickerModalInput {
    tailoredWidgetTemplateList$: Observable<TailoredWidgetTemplate[]>;
    tailoredWidgetList$: Observable<TailoredWidget[]>;
    targetDashboardMode: DashboardMode;
    targetDashboardScopeType: DashboardScopeType;
}

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-picker-modal.scss'],
    selector: 'widget-picker-modal',
    templateUrl: 'widget-picker-modal.html',
    imports: [
        UiFieldComponent,
        AdvizeoInputDirective,
        FormsModule,
        WidgetCatalogItemComponent,
        LoadingAndErrorComponent,
        UiIconButtonComponent,
        AdvizeoTooltipDirective,
        AsyncPipe,
        TranslatePipe,
        ReferenceListLabelPipe,
        ApplyPureFunctionPipe
    ]
})
export class WidgetPickerModalComponent implements AdvizeoModalComponent<WidgetPickerModalInput, TailoredWidgetId>, OnInit, OnDestroy {
    private _tailoredWidgetsProvider = inject(TailoredWidgetsProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _translateService = inject(TranslateService);
    private _disabledFeatureProvider = inject(DisabledFeaturesService);

    title = translate('COMPONENTS.WIDGET_PICKER.TITLE');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([]);

    close = output<TailoredWidgetId | void>();
    input = injectAdvizeoModalInput<WidgetPickerModalInput>();

    tailoredWidgetList$!: Observable<TailoredWidget[]>;
    tailoredWidgetTemplateList$!: Observable<TailoredWidgetTemplate[]>;
    search$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    widget_thumbnail_url: string = 'https://cdn.advizeo.io/img/widgets/widget.svg';

    ngOnDestroy(): void {}

    ngOnInit(): void {
        const search$ = this.search$.pipe(debounceTimeAllButFirst(500), distinctUntilChanged());

        this.tailoredWidgetList$ = combineLatest([this.input().tailoredWidgetList$.pipe(filter(isNotNullish)), search$]).pipe(
            // Filtered with search string
            switchMap(([tailoredWidgetList, searchText]) => {
                return combineLatest(
                    [...tailoredWidgetList]
                        .sort((a, b) => {
                            return (
                                frenchSort(a.type, b.type) ||
                                frenchSort(
                                    this._tailoredWidgetsProvider.getWidgetCatalogRepresentationType(a)!,
                                    this._tailoredWidgetsProvider.getWidgetCatalogRepresentationType(b)!
                                ) ||
                                frenchSort(a.name, b.name)
                            );
                        })
                        .map(widget => this.getSearchableWidgetProperties$(widget))
                ).pipe(
                    map(widgetListWithSearchableProperties => {
                        return widgetListWithSearchableProperties
                            .filter(widget => {
                                return SearchProvider.matchAllWordsStatic(
                                    [
                                        widget.widgetName,
                                        widget.representationTranslation,
                                        widget.widgetTypeTranslation,
                                        widget.dataSourceTranslation,
                                        widget.widgetTemplateTranslation
                                    ],
                                    searchText.split(' ')
                                );
                            })
                            .map(w => w.widget);
                    })
                );
            }),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.tailoredWidgetTemplateList$ = this.input().tailoredWidgetTemplateList$.pipe(
            map(widgets =>
                this._disabledFeatureProvider.isFeatureEnabled(FeatureCode.BUSINESS_INDICATOR_ADVANCEMENT_WIDGET)
                    ? widgets
                    : widgets.filter(w => w.code !== TailoredWidgetTemplateCode.BUSINESS_INDICATOR_ADVANCEMENT)
            ),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }

    private getSearchableWidgetProperties$(widget: TailoredWidget): Observable<{
        representationTranslation: string;
        widgetTypeTranslation: string;
        dataSourceTranslation: string;
        widgetTemplateTranslation: string;
        widgetName: string;
        widget: TailoredWidget;
    }> {
        const representationTranslation$ = this._referenceListsService
            .getReferenceListItem$(
                ReferenceListName.WIDGET_REPRESENTATION_TYPE,
                this._tailoredWidgetsProvider.getWidgetCatalogRepresentationType(widget)!
            )
            .pipe(
                startWith(null),
                map(representationType => (representationType ? representationType.label : ''))
            );

        const widgetTypeTranslation$ = this._translateService.stream('COMPONENTS.WIDGET_PICKER.LABEL.' + widget.type);

        const dataSourceTranslation$ = this._businessIntelligenceProvider
            .getBusinessIntelligenceDataSource$(this._tailoredWidgetsProvider.getWidgetCatalogDataSource(widget)!)
            .pipe(
                startWith(null),
                map(dataSource => (dataSource ? dataSource.name : ''))
            );

        const widgetTemplateTranslation$ =
            widget.configuration.widgetType === TailoredWidgetType.TEMPLATE_WIDGET
                ? this._tailoredWidgetsProvider
                      .getTailoredWidgetTemplateById$(widget.configuration.tailoredWidgetTemplateConfiguration!.widgetTemplateId)
                      .pipe(
                          switchMap(widgetTemplate => {
                              return widgetTemplate
                                  ? this._referenceListsService.getReferenceListItem$(
                                        ReferenceListName.WIDGET_TEMPLATE,
                                        widgetTemplate.code
                                    )
                                  : of(null);
                          }),
                          map(representationType => (representationType ? representationType.label : ''))
                      )
                : of('');

        return combineLatest([representationTranslation$, widgetTypeTranslation$, dataSourceTranslation$, widgetTemplateTranslation$]).pipe(
            map(([representationTranslation, widgetTypeTranslation, dataSourceTranslation, widgetTemplateTranslation]) => {
                return {
                    representationTranslation,
                    widgetTypeTranslation,
                    dataSourceTranslation,
                    widgetTemplateTranslation,
                    widgetName: widget.name,
                    widget
                };
            })
        );
    }

    requestBusinessIntelligenceWidgetCreation() {
        this._tailoredWidgetsProvider
            .requestBusinessIntelligenceWidgetCreation(this.input().targetDashboardMode, this.input().targetDashboardScopeType)
            .then(widgetId => {
                if (widgetId) {
                    this.close.emit(widgetId);
                }
            });
    }

    requestWidgetCreationFromTemplate(templateWidgetId: TailoredWidgetTemplateId) {
        this._tailoredWidgetsProvider
            .requestWidgetCreationFromTemplate(templateWidgetId, this.input().targetDashboardMode, this.input().targetDashboardScopeType)
            .then(widgetId => this.close.emit(widgetId));
    }

    onTailoredWidgetSelection(tailoredWidgetId: number) {
        this.close.emit(tailoredWidgetId as TailoredWidgetId);
    }

    requestWidgetEdition(tailoredWidgetId: TailoredWidgetId) {
        this._tailoredWidgetsProvider.requestWidgetEdition(
            tailoredWidgetId,
            this.input().targetDashboardMode,
            this.input().targetDashboardScopeType
        );
    }

    requestWidgetDuplication(tailoredWidgetId: TailoredWidgetId) {
        this._tailoredWidgetsProvider
            .requestWidgetDuplication(tailoredWidgetId, this.input().targetDashboardMode, this.input().targetDashboardScopeType)
            .then(widgetId => {
                if (widgetId) {
                    this.close.emit(widgetId);
                }
            });
    }

    requestWidgetDeletion(tailoredWidgetId: TailoredWidgetId) {
        this._tailoredWidgetsProvider.requestWidgetDeletion(tailoredWidgetId);
    }

    isWidgetEditable$ = ({
        widgetId,
        dashboardType
    }: {
        widgetId: TailoredWidgetId;
        dashboardType: DashboardScopeType;
    }): Observable<boolean> => {
        return this._tailoredWidgetsProvider.isWidgetEditable$(widgetId, dashboardType);
    };

    isWidgetDuplicable$ = ({
        widgetId,
        dashboardType
    }: {
        widgetId: TailoredWidgetId;
        dashboardType: DashboardScopeType;
    }): Observable<boolean> => {
        return this._tailoredWidgetsProvider.isWidgetDuplicable$(widgetId, dashboardType).pipe(map(isWidgetEditable => isWidgetEditable));
    };

    isWidgetDeletable$ = ({
        widgetId,
        dashboardType
    }: {
        widgetId: TailoredWidgetId;
        dashboardType: DashboardScopeType;
    }): Observable<boolean> => {
        return this._tailoredWidgetsProvider.isWidgetDeletable$(widgetId, dashboardType);
    };

    getThumbnailUrl$ = (widgetId: TailoredWidgetId): Observable<string> => {
        return this._tailoredWidgetsProvider.getThumbnailUrl$(widgetId);
    };

    getTemplateThumbnailUrl$ = (widgetTemplateId: TailoredWidgetTemplateId): Observable<string> => {
        return this._tailoredWidgetsProvider.getTemplateThumbnailUrl$(widgetTemplateId);
    };
}
